// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Link from "@insite/mobius/Link";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import JettingAppDropDown, {
    JettingAppDropDownStyles,
} from "@stoneage/App/Components/JettingAppComponents/JettingAppDropDown";
import JettingAppTextField, {
    JettingAppTextFieldStyles,
} from "@stoneage/App/Components/JettingAppComponents/JettingAppTextField";
import { HoseArray } from "@stoneage/App/Services/JettingApp/JettingAppService";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import Button, { ButtonIcon, ButtonPresentationProps } from "@stoneage/MobiusOverrides/Button/Button";
import Icon from "@stoneage/MobiusOverrides/Icon";
import { LinkPresentationProps } from "@stoneage/MobiusOverrides/Link";
import { MessageType, MessageVariant, messageVariants } from "@stoneage/XDL/constants";
import IconClear from "@stoneage/XDL/Icons/IconClear";
import React, { useEffect, useState } from "react";

interface Props {
    tooltipText: string;
    currentUnit?: string;
    units: { [key: string]: string };
    message?: { message: string; type: string };
    hoseArray: HoseArray[];
    hoseOptions: { hoseID: string; hoseDescription: string; sortOrder: number }[];
    onChange: (newHoseArray: HoseArray[]) => void;
    extendedStyles?: JettingAppHosesStyles;
    readOnly: boolean;
}

export interface JettingAppHosesStyles {
    wrapper?: InjectableCss;
    dropDown?: JettingAppDropDownStyles;
    tooltip?: TooltipPresentationProps;
    addLink?: LinkPresentationProps;
    rowWrapper?: InjectableCss;
    inputsWrapper?: InjectableCss;
    rowTitle?: TypographyPresentationProps;
    textFieldStyles?: JettingAppTextFieldStyles;
    linkWrapper?: InjectableCss;
    removeButton?: ButtonPresentationProps;
    removeWrapper?: InjectableCss;
    messageStyle?: TypographyPresentationProps;
    messageWrapper?: InjectableCss;
    message?: TypographyPresentationProps;
}

export const jettingAppHosesStyles: JettingAppHosesStyles = {};

interface HoseFieldState {
    [index: string]: {
        hoseID: string;
        hoseLength: number | undefined;
        index: number;
    };
}

const JettingAppHoses = ({
    extendedStyles,
    tooltipText,
    onChange,
    hoseArray,
    hoseOptions,
    currentUnit,
    units,
    message,
    readOnly,
}: Props) => {
    const [styles] = useState(() => mergeToNew(jettingAppHosesStyles, extendedStyles));
    const [localHoseArray, setLocalHoseArray] = useState<HoseFieldState>({});
    const isTabletScreen = useAppSelector(state => (state as CustomAppState).xCommon.isTabletScreen);

    useEffect(() => {
        const tempHoseArray: HoseFieldState = {};
        for (let i = 0; i < hoseArray?.length; i++) {
            tempHoseArray[i] = {
                hoseID: hoseArray[i].hoseID,
                hoseLength: hoseArray[i].hoseLength,
                index: i,
            };
        }
        setLocalHoseArray(tempHoseArray);
    }, [hoseArray, hoseArray?.length]);

    const onDropDownChange = (event: React.ChangeEvent<HTMLSelectElement>, index: number) => {
        const value = event.currentTarget.value;
        setLocalHoseArray(prevState => {
            const newState = { ...prevState };
            newState[index] = { ...newState[index], hoseID: value };
            invokeOnChange(newState);
            return newState;
        });
    };

    const onTextFieldChange = (value: number, index: number) => {
        setLocalHoseArray(prevState => {
            const newState = { ...prevState };
            newState[index] = { ...newState[index], hoseLength: value };
            invokeOnChange(newState);
            return newState;
        });
    };

    const removeHose = (id: number) => {
        setLocalHoseArray(prevState => {
            const newState = { ...prevState };
            delete newState[id];
            invokeOnChange(newState);
            return newState;
        });
    };

    const addNewHose = () => {
        const newHoseIndex = Object.keys(localHoseArray).length;
        setLocalHoseArray(prevState => {
            const newState = { ...prevState };
            newState[newHoseIndex] = { hoseID: "", hoseLength: undefined, index: newHoseIndex };
            invokeOnChange(newState);
            return newState;
        });
    };

    const invokeOnChange = (newHoseArray: HoseFieldState) => {
        if (!newHoseArray || !Object.keys(newHoseArray)) {
            return;
        }
        // we need to call onChange function, which will invoke the api
        const updatedHoseArray = Object.values(newHoseArray)?.map(({ index, ...rest }) => rest);
        updatedHoseArray && onChange && onChange(updatedHoseArray);
    };

    if (!localHoseArray || !Object.keys(localHoseArray)?.length) {
        return null;
    }

    const variant: MessageVariant = messageVariants[message?.type as MessageType] ?? {
        icon: undefined,
        color: undefined,
        bgColor: "transparent",
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            {Object.keys(localHoseArray).map((key, index) => {
                const hose = localHoseArray[key];
                return (
                    <StyledWrapper {...styles.rowWrapper} key={index}>
                        <Typography {...styles.rowTitle}>
                            {translate("Hose/Lance")} {index + 1}
                        </Typography>
                        <StyledWrapper {...styles.inputsWrapper}>
                            <JettingAppTextField
                                label={index === 0 || isTabletScreen ? translate("Length") : ""}
                                value={hose.hoseLength}
                                currentUnit={currentUnit}
                                units={units}
                                onChange={(value: number) => onTextFieldChange(value, index)}
                                labelIsHorizontal={false}
                                extendedStyles={styles.textFieldStyles}
                                tooltipText={index === 0 ? tooltipText : ""}
                                readOnly={readOnly}
                            />
                            <JettingAppDropDown
                                optionType="hose"
                                label={index === 0 || isTabletScreen ? translate("Size") : ""}
                                value={hose.hoseID}
                                onChange={event => onDropDownChange(event, index)}
                                options={[...hoseOptions].sort((a, b) => a.sortOrder - b.sortOrder)}
                                extendedStyles={styles.dropDown}
                                readOnly={readOnly}
                            />
                            <StyledWrapper {...styles.removeWrapper}>
                                {index > 0 && (
                                    <Button showIcon={false} {...styles.removeButton} onClick={() => removeHose(index)}>
                                        <ButtonIcon size={13} src={IconClear} />
                                    </Button>
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                );
            })}

            {message?.message && (
                <StyledWrapper {...styles.messageWrapper} {...{ bgColor: variant.bgColor || "transparent" }}>
                    <Icon src={variant.icon} color={variant.color} size={16} />
                    <Typography {...styles.message}>{message.message}</Typography>
                </StyledWrapper>
            )}

            <StyledWrapper {...styles.linkWrapper}>
                <Link {...styles.addLink} onClick={addNewHose}>
                    {translate("Add Another Hose/Lance")}
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};
export default JettingAppHoses;
